/**
 * Responsibility: the screen you see when you are not signed in, and the gate
 * that decides whether you see it.
 *
 * Deliberately one small file rather than a login page plus a route guard.
 * There is exactly one way in and one thing to look at while deciding, and
 * splitting them would mean two files that only ever appear together.
 *
 * **Three states, not two.** Signed in, signed out, and *not yet known* - the
 * tick after a reload while Firebase restores a stored session. Treating the
 * third as signed out makes every refresh flash a login screen at somebody who
 * never left, which reads as being logged out at random.
 *
 * A build with no Firebase config says so plainly instead of offering a button
 * that cannot work. That is the state of a fresh checkout and of CI, and it
 * should be legible rather than looking like a broken sign-in.
 */
import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAuth } from './authContext';

function Centred({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      {children}
    </Box>
  );
}

function SignInCard() {
  const { signIn, configured } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const attempt = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await signIn();
    } catch (error) {
      // A closed popup is the common case and is not an error worth shouting
      // about; anything else is worth showing verbatim.
      const message = error instanceof Error ? error.message : String(error);
      setFailed(message.includes('popup-closed') ? null : message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 4, maxWidth: 420, width: '100%' }}>
      <Stack spacing={2.5}>
        <Stack spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            dropmon
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Restock and trading-card intelligence. Sign in to continue.
          </Typography>
        </Stack>

        {!configured ? (
          <Alert severity="warning">
            This build carries no Firebase configuration, so there is nothing to
            sign in to. Set the <code>VITE_FIREBASE_*</code> variables and
            rebuild.
          </Alert>
        ) : null}

        {failed ? <Alert severity="error">{failed}</Alert> : null}

        <Button
          variant="contained"
          size="large"
          disabled={!configured || busy}
          onClick={() => void attempt()}
        >
          {busy ? 'Signing in' : 'Continue with Google'}
        </Button>

        <Typography variant="caption" color="text.secondary">
          Accounts are invited. A Google address that has not been added will be
          refused after signing in.
        </Typography>
      </Stack>
    </Paper>
  );
}

/** Renders its children only for a signed-in person. */
export function SignInGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Centred>
        <CircularProgress />
      </Centred>
    );
  }

  if (!user) {
    return (
      <Centred>
        <SignInCard />
      </Centred>
    );
  }

  return <>{children}</>;
}
